import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { listDNC, addDNC, removeDNC, exportDNC } from "../api/contacts";
import { normalizePhone } from "../lib/phone";

export function DncPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const perPage = 50;

  async function download() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await exportDNC(search || undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dnc-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError((e as Error)?.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  }

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dnc", page, search],
    queryFn: () => listDNC({ page, per_page: perPage, search: search || undefined }),
    placeholderData: keepPreviousData,
  });

  const add = useMutation({
    mutationFn: () =>
      addDNC({ phone: normalizePhone(newPhone), description: newDesc || undefined }),
    onSuccess: () => {
      setNewPhone("");
      setNewDesc("");
      qc.invalidateQueries({ queryKey: ["dnc"] });
    },
  });

  const remove = useMutation({
    mutationFn: (phone: string) => removeDNC({ phones: [phone] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dnc"] }),
  });

  if (isLoading) return <p className="text-gray-500">Loading Do-Not-Call list…</p>;
  if (isError) return <p className="text-red-600">{(error as Error).message}</p>;

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Do-Not-Call List</h1>
      <p className="text-sm text-gray-500 mb-4">
        Entries on your account — blocked from all of your campaigns. {total.toLocaleString()} entries.
        Platform-wide suppression lists (regulatory, litigation and complaint lists) also apply to
        every campaign automatically; they are managed by the platform and are not part of this list.
      </p>

      <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Add number</label>
          <input
            className="border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="Phone number"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Reason (optional)</label>
          <input
            className="border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="e.g. customer request"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
          />
        </div>
        <button
          disabled={!newPhone || add.isPending}
          onClick={() => add.mutate()}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
        >
          Add to DNC
        </button>
        <div className="ml-auto flex items-end gap-3">
          <button
            onClick={download}
            disabled={downloading || total === 0}
            title="Download the entries shown (respects the current search) as a CSV file"
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 rounded px-4 py-2 text-sm disabled:opacity-50"
          >
            {downloading ? "Preparing…" : "Download CSV"}
          </button>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            className="border border-gray-300 rounded px-3 py-2 text-sm w-64"
            placeholder="Phone or email…"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
        </div>
      </div>

      {downloadError && <p className="text-sm text-red-600 mb-4 -mt-2">{downloadError}</p>}

      {add.isError && (
        <p className="text-sm text-red-600 mb-4 -mt-2">
          {(add.error as Error)?.message ||
            "Couldn't add that number — enter a valid 10-digit US phone number."}
        </p>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Phone</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Reason</th>
              <th className="px-4 py-2">Scope</th>
              <th className="px-4 py-2">Added</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.contact_dnc_id}>
                <td className="px-4 py-2 font-mono">{r.phone || "—"}</td>
                <td className="px-4 py-2">{r.email || "—"}</td>
                <td className="px-4 py-2 text-gray-500">{r.description || "—"}</td>
                <td className="px-4 py-2">
                  {r.is_global ? (
                    <span className="text-xs bg-amber-100 text-amber-700 rounded px-2 py-0.5">global</span>
                  ) : (
                    <span className="text-xs text-gray-400">own</span>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-400 font-mono text-xs">{r.created_at?.slice(0, 16) || "—"}</td>
                <td className="px-4 py-2 text-right">
                  {r.phone && !r.is_global && (
                    <button
                      onClick={() => {
                        if (confirm(`Remove ${r.phone} from the DNC list?`)) remove.mutate(r.phone!);
                      }}
                      className="text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-gray-400 text-sm p-4">No matching entries.</p>}
      </div>

      <div className="flex items-center justify-between mt-3 text-sm">
        <span className="text-gray-500">
          Page {page} of {totalPages}
        </span>
        <div className="space-x-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
